import React, { useState } from 'react';
import { X, Eye, EyeOff, Lock, Check } from 'lucide-react';
import { supabase } from '../../supabaseClient';

function ChangePinModal({ establishmentId, establishmentName, onClose, onSuccess }) {
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const WEAK_PINS = ['0000', '1111', '2222', '3333', '4444', '5555',
                     '6666', '7777', '8888', '9999', '1234', '4321'];

  const validatePin = () => {
    if (!/^\d{4,6}$/.test(newPin)) {
      return 'PIN must be 4-6 digits';
    }
    if (WEAK_PINS.includes(newPin)) {
      return 'Weak PIN detected. Please choose a different PIN';
    }
    if (newPin !== confirmPin) {
      return 'PINs do not match';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validatePin();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc('change_establishment_pin', {
        establishment_id: establishmentId,
        new_pin: newPin
      });

      if (rpcError) throw rpcError;
      if (data && !data.success) throw new Error(data.error);

      setSuccess(true);
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose();
      }, 2000);

    } catch (err) {
      setError(err.message || 'Failed to change PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div className="card" style={{ width: '100%', maxWidth: '420px', padding: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '1.1rem', fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Lock size={20} color="var(--accent-primary)" /> Change PIN
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
            <X size={20} />
          </button>
        </div>

        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '24px' }}>
          Change the PIN code for <strong>{establishmentName}</strong>
        </p>

        {success ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Check size={48} color="var(--accent-success)" style={{ margin: '0 auto 16px' }} />
            <p style={{ color: 'var(--accent-success)', fontWeight: 500 }}>PIN changed successfully!</p>
          </div>
        ) : (
          <>
            {error && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--accent-danger)', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">New PIN (4-6 digits)</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPin ? 'text' : 'password'}
                  className="form-input"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter new PIN"
                  maxLength={6}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                >
                  {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Confirm PIN</label>
              <input
                type={showPin ? 'text' : 'password'}
                className="form-input"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Re-enter new PIN"
                maxLength={6}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button className="btn" style={{ flex: 1 }} onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={handleSubmit}
                disabled={loading || !newPin || !confirmPin}
              >
                {loading ? 'Changing...' : 'Change PIN'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChangePinModal;
